import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { challengeAPI, chatAPI, discussionAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const SKILL_META: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  listening: { label: 'Listening', icon: 'headphones', color: '#006571', bg: '#e6fafb' },
  reading:   { label: 'Reading',   icon: 'menu_book',  color: '#004be2', bg: '#e8eeff' },
  writing:   { label: 'Writing',   icon: 'edit_note',  color: '#8e00d5', bg: '#f5e6ff' },
  speaking:  { label: 'Speaking',  icon: 'mic',        color: '#b41340', bg: '#ffe8ed' },
  any:       { label: 'Mọi kỹ năng', icon: 'emoji_events', color: '#b45309', bg: '#fef3c7' },
}

const METRIC_LABELS: Record<string, string> = {
  sessions_completed: 'buổi học',
  xp_earned: 'XP',
  questions_answered: 'câu hỏi',
}

/** FR-015 — Weekly Challenge & Community */
export default function ChallengePage() {
  const [tab, setTab] = useState<'challenge' | 'leaderboard' | 'buddy' | 'discussion'>('challenge')
  const qc = useQueryClient()

  const { data: challenge, isLoading } = useQuery({
    queryKey: ['challenge-current'],
    queryFn: challengeAPI.getCurrent,
  })

  const joinMutation = useMutation({
    mutationFn: challengeAPI.join,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['challenge-current'] }),
  })

  const syncMutation = useMutation({
    mutationFn: challengeAPI.syncProgress,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['challenge-current'] }),
  })

  const tabs = [
    { id: 'challenge' as const, label: 'Thử thách tuần', icon: 'emoji_events' },
    { id: 'leaderboard' as const, label: 'Bảng xếp hạng', icon: 'leaderboard' },
    { id: 'discussion' as const, label: 'Thảo luận', icon: 'forum' },
    { id: 'buddy' as const, label: 'Study Buddy', icon: 'group' },
  ]

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#b45309', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>emoji_events</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#b45309' }}>Cộng đồng</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Thử thách & <span style={{ color: '#b45309', fontStyle: 'italic' }}>Cộng đồng</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Thi đua tuần, bảng xếp hạng XP và ghép đôi Study Buddy cùng mục tiêu.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '2rem' }}
        className="challenge-tabs"
      >
        {tabs.map(t => {
          const sel = tab === t.id
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="challenge-tab-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                padding: '0.75rem 1rem', borderRadius: '1rem', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                background: sel ? 'linear-gradient(135deg, #b45309, #fbbf24)' : 'white',
                boxShadow: sel ? '0 8px 20px rgba(180,83,9,0.3)' : '0 2px 8px rgba(0,0,0,0.04)',
                color: sel ? 'white' : '#535b71',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: sel ? "'FILL' 1" : "'FILL' 0", flexShrink: 0 }}>{t.icon}</span>
              <H style={{ fontWeight: 700, fontSize: '0.875rem', color: 'inherit' }}>{t.label}</H>
            </button>
          )
        })}
      </div>

      {tab === 'challenge' && (
        isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <div className="spinner" />
          </div>
        ) : challenge ? (
          <ChallengeCard
            challenge={challenge}
            onJoin={() => joinMutation.mutate()}
            onSync={() => syncMutation.mutate()}
            joining={joinMutation.isPending}
            syncing={syncMutation.isPending}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '4rem', background: 'white', borderRadius: '1.5rem' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>pending</span>
            <H style={{ display: 'block', fontWeight: 700, color: '#535b71' }}>Chưa có thử thách tuần này</H>
            <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Quay lại vào đầu tuần nhé!</p>
          </div>
        )
      )}

      {tab === 'leaderboard' && <LeaderboardPanel />}
      {tab === 'discussion' && <DiscussionPanel />}
      {tab === 'buddy' && <BuddyPanel />}
    </AppLayout>
  )
}

// ─── Challenge Card ───────────────────────────────────────────────────────────

function ChallengeCard({ challenge, onJoin, onSync, joining, syncing }: {
  challenge: any; onJoin: () => void; onSync: () => void; joining: boolean; syncing: boolean
}) {
  const { title, description, skill, targetMetric, targetValue, participation } = challenge
  const progress = participation?.progress ?? 0
  const completed = participation?.completed ?? false
  const joined = participation !== null
  const progressPct = Math.min(100, Math.round((progress / targetValue) * 100))
  const skillCfg = SKILL_META[skill] ?? SKILL_META.any

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 20rem', gap: '1.5rem', alignItems: 'start' }}>
      {/* Main card */}
      <div style={{ background: 'linear-gradient(135deg, #b45309, #fbbf24)', borderRadius: '2rem', padding: '2.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: '-3rem', right: '-3rem', width: '14rem', height: '14rem', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', position: 'relative' }}>
          <span style={{ padding: '0.375rem 1rem', borderRadius: '9999px', background: 'rgba(255,255,255,0.2)', fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'white' }}>Thử thách tuần này</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.875rem', borderRadius: '9999px', background: skillCfg.bg }}>
            <span className="material-symbols-outlined" style={{ color: skillCfg.color, fontSize: '0.875rem', fontVariationSettings: "'FILL' 1" }}>{skillCfg.icon}</span>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: skillCfg.color }}>{skillCfg.label}</span>
          </div>
        </div>

        <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: 'white', lineHeight: 1.2, marginBottom: '0.75rem', position: 'relative' }}>
          {title}
        </H>
        <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, marginBottom: '2rem', position: 'relative' }}>{description}</p>

        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '1rem', padding: '1.25rem', marginBottom: '2rem', position: 'relative' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.375rem' }}>Mục tiêu</span>
          <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 900, color: 'white' }}>
            {targetValue} {METRIC_LABELS[targetMetric] ?? targetMetric}
          </H>
        </div>

        {joined && (
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem', fontWeight: 600 }}>
                {progress} / {targetValue} {METRIC_LABELS[targetMetric]}
              </span>
              <span style={{ color: 'white', fontWeight: 800 }}>{progressPct}%</span>
            </div>
            <div style={{ width: '100%', height: '0.625rem', background: 'rgba(255,255,255,0.2)', borderRadius: '9999px', overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{ width: `${progressPct}%`, height: '100%', background: 'white', borderRadius: '9999px', transition: 'width 0.5s' }} />
            </div>
            {completed && (
              <div style={{ padding: '1rem', background: 'rgba(255,255,255,0.2)', borderRadius: '1rem', textAlign: 'center', color: 'white', fontWeight: 700 }}>
                🎉 Đã hoàn thành! Nhận huy hiệu tuần này.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', position: 'relative' }}>
          {!joined ? (
            <button
              onClick={onJoin} disabled={joining}
              style={{ padding: '1rem 2rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'white', color: '#b45309', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              {joining ? 'Đang tham gia...' : <><span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>add_circle</span> Tham gia thử thách</>}
            </button>
          ) : (
            <button
              onClick={onSync} disabled={syncing}
              style={{ padding: '1rem 2rem', borderRadius: '9999px', border: '2px solid rgba(255,255,255,0.4)', background: 'transparent', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.125rem' }}>sync</span>
              {syncing ? 'Đang cập nhật...' : 'Đồng bộ tiến độ'}
            </button>
          )}
        </div>
      </div>

      {/* Sidebar tips */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '0.9375rem', marginBottom: '1rem' }}>Phần thưởng</H>
          {[
            { icon: 'emoji_events', label: 'Huy hiệu tuần',  color: '#b45309' },
            { icon: 'bolt',         label: '+200 XP bonus',  color: '#004be2' },
            { icon: 'star',         label: 'Xếp hạng cao hơn', color: '#8e00d5' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span className="material-symbols-outlined" style={{ color: item.color, fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#272e42' }}>{item.label}</span>
            </div>
          ))}
        </div>
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
          <H style={{ display: 'block', fontWeight: 800, fontSize: '0.9375rem', marginBottom: '1rem', color: '#004be2' }}>Mẹo chinh phục</H>
          {['Học đều mỗi ngày thay vì dồn cuối tuần', 'Hoàn thành phiên học trước 21:00', 'Kết hợp nhiều kỹ năng để tăng XP nhanh'].map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.625rem' }}>
              <span style={{ flexShrink: 0, width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: '#004be2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 800 }}>{i + 1}</span>
              <span style={{ fontSize: '0.8125rem', color: '#535b71', lineHeight: 1.5 }}>{tip}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Leaderboard ──────────────────────────────────────────────────────────────

function LeaderboardPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['challenge-leaderboard'],
    queryFn: challengeAPI.getLeaderboard,
  })

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
  if (!data) return null

  const medals = ['🥇', '🥈', '🥉']

  return (
    <div>
      {/* My rank */}
      {data.me?.weeklyXp > 0 && (
        <div style={{ background: 'linear-gradient(135deg, #004be2, #809bff)', borderRadius: '1.5rem', padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
          <div>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.75, display: 'block', marginBottom: '0.25rem' }}>Thứ hạng của bạn</span>
            <H style={{ fontSize: '2rem', fontWeight: 900, color: 'white' }}>#{data.me.rank ?? '—'}</H>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, opacity: 0.75, display: 'block', marginBottom: '0.25rem' }}>XP tuần này</span>
            <H style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white' }}>{data.me.weeklyXp}</H>
          </div>
        </div>
      )}

      {/* Leaderboard list */}
      <div style={{ background: 'white', borderRadius: '1.5rem', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
        <div style={{ padding: '1.5rem 2rem', background: '#eef0ff' }}>
          <H style={{ fontWeight: 800 }}>Top XP tuần này</H>
        </div>
        {data.leaderboard.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#535b71' }}>
            Chưa có ai tích XP tuần này. Hãy là người đầu tiên!
          </div>
        ) : (
          <div>
            {data.leaderboard.map((entry: any, i: number) => (
              <div
                key={entry.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 2rem',
                  background: i < 3 ? `rgba(${i === 0 ? '180,83,9' : i === 1 ? '107,114,128' : '180,19,64'}, 0.04)` : 'transparent',
                  borderBottom: '1px solid #f6f6ff',
                }}
              >
                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: '1.125rem', minWidth: '2rem', textAlign: 'center' }}>
                  {i < 3 ? medals[i] : `#${entry.rank}`}
                </span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{entry.name}</span>
                </div>
                <H style={{ fontWeight: 900, color: '#004be2' }}>{entry.weeklyXp} <span style={{ fontWeight: 400, color: '#a5adc6', fontSize: '0.8125rem' }}>XP</span></H>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Study Buddy ──────────────────────────────────────────────────────────────

function BuddyPanel() {
  const qc = useQueryClient()
  const [message, setMessage] = useState('')
  const [lastSince, setLastSince] = useState<string | undefined>(undefined)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: buddy, isLoading } = useQuery({
    queryKey: ['study-buddy'],
    queryFn: challengeAPI.getBuddy,
  })

  const { data: chatData, refetch: refetchChat } = useQuery({
    queryKey: ['buddy-chat'],
    queryFn: () => chatAPI.getMessages(),
    enabled: !!buddy,
    refetchInterval: buddy ? 5000 : false, // poll every 5s
  })

  const request = useMutation({
    mutationFn: challengeAPI.requestBuddy,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['study-buddy'] }),
  })

  const sendMutation = useMutation({
    mutationFn: () => chatAPI.sendMessage(message),
    onSuccess: () => {
      setMessage('')
      refetchChat()
    },
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatData?.messages?.length])

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>

  const messages = chatData?.messages ?? []

  return (
    <div style={{ display: 'grid', gridTemplateColumns: buddy ? '1fr 1.5fr' : '1fr', gap: '1.5rem', maxWidth: '56rem' }}>
      {/* Buddy info */}
      <div style={{ background: 'white', borderRadius: '2rem', padding: '2.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', textAlign: 'center' }}>
        <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', boxShadow: '0 8px 24px rgba(142,0,213,0.3)' }}>
          <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '2rem', fontVariationSettings: "'FILL' 1" }}>group</span>
        </div>
        <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 900, marginBottom: '0.5rem' }}>Study Buddy</H>
        <p style={{ color: '#535b71', lineHeight: 1.7, marginBottom: '2rem' }}>
          Ghép đôi với người học cùng mục tiêu để cùng tiến bộ và giữ động lực.
        </p>

        {buddy ? (
          <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, color: 'white', fontSize: '1.25rem' }}>
                {buddy.name?.[0]?.toUpperCase() ?? '?'}
              </div>
              <div>
                <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem' }}>{buddy.name}</H>
                <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>Mục tiêu: <strong>{buddy.targetLevel}</strong></span>
              </div>
            </div>
            {buddy.examDate && (
              <p style={{ fontSize: '0.8125rem', color: '#535b71' }}>📅 Ngày thi: {buddy.examDate}</p>
            )}
            <p style={{ marginTop: '1rem', fontSize: '0.9375rem', fontWeight: 700, color: '#8e00d5', textAlign: 'center' }}>Cùng nhau cố gắng tuần này! 💪</p>
          </div>
        ) : request.data?.status === 'waiting' ? (
          <div style={{ padding: '1.5rem', background: '#f5e6ff', borderRadius: '1.5rem' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }} />
            <H style={{ display: 'block', fontWeight: 800, color: '#8e00d5', marginBottom: '0.5rem' }}>Đang tìm buddy phù hợp...</H>
            <p style={{ color: '#535b71', fontSize: '0.875rem' }}>Chúng tôi sẽ ghép bạn với người có cùng mục tiêu VSTEP.</p>
          </div>
        ) : (
          <>
            <button
              onClick={() => request.mutate()}
              disabled={request.isPending}
              style={{
                width: '100%', padding: '1.125rem', borderRadius: '9999px', border: 'none',
                background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', color: 'white',
                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem',
                cursor: 'pointer', boxShadow: '0 8px 24px rgba(142,0,213,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem',
              }}
            >
              {request.isPending ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang tìm...</> : <><span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>handshake</span> Tìm Study Buddy</>}
            </button>
            {request.isError && (
              <p style={{ marginTop: '1rem', color: '#b41340', fontSize: '0.875rem', fontWeight: 600 }}>
                {(request.error as Error).message}
              </p>
            )}
          </>
        )}
      </div>

      {/* Chat panel — only shown when buddy exists */}
      {buddy && (
        <div style={{ background: 'white', borderRadius: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', height: '32rem' }}>
          {/* Chat header */}
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #eef0ff', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.875rem' }}>
              {buddy.name?.[0]?.toUpperCase() ?? '?'}
            </div>
            <div>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '0.9375rem' }}>{buddy.name}</H>
              <span style={{ fontSize: '0.75rem', color: '#a5adc6' }}>Study Buddy tuần này</span>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {messages.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a5adc6', fontSize: '0.875rem', textAlign: 'center' }}>
                <div>
                  <span className="material-symbols-outlined" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.5rem' }}>chat_bubble_outline</span>
                  Bắt đầu trò chuyện với buddy của bạn!
                </div>
              </div>
            ) : (
              messages.map((msg: any) => (
                <div key={msg.id} style={{ display: 'flex', justifyContent: msg.isMine ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '75%', padding: '0.625rem 1rem', borderRadius: msg.isMine ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                    background: msg.isMine ? 'linear-gradient(135deg, #8e00d5, #eac4ff)' : '#f6f6ff',
                    color: msg.isMine ? 'white' : '#272e42',
                    fontSize: '0.9375rem', lineHeight: 1.5,
                  }}>
                    {msg.content}
                    <div style={{ fontSize: '0.6875rem', opacity: 0.65, marginTop: '0.25rem', textAlign: 'right' }}>
                      {new Date(msg.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #eef0ff', display: 'flex', gap: '0.625rem' }}>
            <input
              type="text"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && message.trim()) { e.preventDefault(); sendMutation.mutate() } }}
              placeholder="Nhắn tin với buddy..."
              style={{ flex: 1, padding: '0.75rem 1rem', borderRadius: '9999px', border: '2px solid #d9e2ff', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', transition: 'border-color 0.2s' }}
              onFocus={e => { e.target.style.borderColor = '#8e00d5' }}
              onBlur={e => { e.target.style.borderColor = '#d9e2ff' }}
            />
            <button
              onClick={() => sendMutation.mutate()}
              disabled={!message.trim() || sendMutation.isPending}
              style={{ width: '2.75rem', height: '2.75rem', borderRadius: '50%', border: 'none', background: message.trim() ? 'linear-gradient(135deg, #8e00d5, #eac4ff)' : '#d9e2ff', color: 'white', cursor: message.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>send</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Discussion Board ─────────────────────────────────────────────────────────

function DiscussionPanel() {
  const qc = useQueryClient()
  const [newPost, setNewPost] = useState('')
  const [replyTo, setReplyTo] = useState<{ id: string; userName: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['discussion-current'],
    queryFn: discussionAPI.getCurrent,
  })

  const createMutation = useMutation({
    mutationFn: () => discussionAPI.createPost(newPost, replyTo?.id),
    onSuccess: () => {
      setNewPost('')
      setReplyTo(null)
      qc.invalidateQueries({ queryKey: ['discussion-current'] })
    },
  })

  const likeMutation = useMutation({
    mutationFn: (postId: string) => discussionAPI.likePost(postId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discussion-current'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (postId: string) => discussionAPI.deletePost(postId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discussion-current'] }),
  })

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>

  const posts = data?.posts ?? []
  const topLevel = posts.filter((p: any) => !p.parentId)
  const replies = posts.filter((p: any) => p.parentId)

  return (
    <div style={{ maxWidth: '48rem' }}>
      {/* Compose */}
      <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
        {replyTo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', padding: '0.5rem 0.875rem', background: '#eef0ff', borderRadius: '0.625rem' }}>
            <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>Trả lời <strong>{replyTo.userName}</strong></span>
            <button onClick={() => setReplyTo(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6', fontSize: '1rem', lineHeight: 1 }}>×</button>
          </div>
        )}
        <textarea
          value={newPost}
          onChange={e => setNewPost(e.target.value)}
          placeholder="Chia sẻ kinh nghiệm, hỏi đáp, hoặc cổ vũ mọi người..."
          rows={3}
          style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.75rem', background: '#f6f6ff', border: '2px solid #d9e2ff', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', resize: 'vertical', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
          onFocus={e => { e.target.style.borderColor = '#b45309' }}
          onBlur={e => { e.target.style.borderColor = '#d9e2ff' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!newPost.trim() || createMutation.isPending}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: 'none', background: newPost.trim() ? 'linear-gradient(135deg, #b45309, #fbbf24)' : '#d9e2ff', color: newPost.trim() ? 'white' : '#a5adc6', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem', cursor: newPost.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {createMutation.isPending ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang đăng...</> : <><span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>send</span> Đăng</>}
          </button>
        </div>
      </div>

      {/* Posts */}
      {topLevel.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '1.5rem' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>forum</span>
          <H style={{ display: 'block', fontWeight: 700, color: '#535b71' }}>Chưa có bài đăng nào</H>
          <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Hãy là người đầu tiên chia sẻ!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {topLevel.map((post: any) => {
            const postReplies = replies.filter((r: any) => r.parentId === post.id)
            return (
              <div key={post.id} style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.875rem', marginBottom: '0.875rem' }}>
                  <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', background: 'linear-gradient(135deg, #b45309, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.875rem', flexShrink: 0 }}>
                    {post.userName?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                      <H style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{post.userName}</H>
                      <span style={{ fontSize: '0.75rem', color: '#a5adc6' }}>
                        {new Date(post.createdAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.9375rem', color: '#272e42', lineHeight: 1.6, margin: 0 }}>{post.content}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #f6f6ff' }}>
                  <button
                    onClick={() => likeMutation.mutate(post.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: 'none', cursor: 'pointer', color: post.likedByMe ? '#b45309' : '#a5adc6', fontWeight: 700, fontSize: '0.8125rem', padding: '0.25rem 0.5rem', borderRadius: '0.5rem', transition: 'all 0.15s' }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: post.likedByMe ? "'FILL' 1" : "'FILL' 0" }}>favorite</span>
                    {post.likes}
                  </button>
                  <button
                    onClick={() => setReplyTo({ id: post.id, userName: post.userName })}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6', fontWeight: 700, fontSize: '0.8125rem', padding: '0.25rem 0.5rem', borderRadius: '0.5rem' }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>reply</span>
                    Trả lời
                  </button>
                  {post.isMine && (
                    <button
                      onClick={() => deleteMutation.mutate(post.id)}
                      style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6', fontSize: '0.75rem', padding: '0.25rem 0.5rem', borderRadius: '0.5rem' }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>delete</span>
                    </button>
                  )}
                </div>
                {/* Replies */}
                {postReplies.length > 0 && (
                  <div style={{ marginTop: '0.875rem', paddingLeft: '1.5rem', borderLeft: '2px solid #eef0ff', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {postReplies.map((reply: any) => (
                      <div key={reply.id} style={{ display: 'flex', gap: '0.75rem' }}>
                        <div style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', background: '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.75rem', flexShrink: 0 }}>
                          {reply.userName?.[0]?.toUpperCase() ?? '?'}
                        </div>
                        <div>
                          <H style={{ fontWeight: 700, fontSize: '0.8125rem', color: '#004be2' }}>{reply.userName}</H>
                          <p style={{ fontSize: '0.875rem', color: '#272e42', lineHeight: 1.5, margin: '0.125rem 0 0' }}>{reply.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
